import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['dropzone', 'fileInput', 'preview']

  connect() {
    // Initialize drag and drop prevention on document
    this.preventDefaults = this.preventDefaults.bind(this);
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })
  }

  disconnect() {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      document.body.removeEventListener(eventName, this.preventDefaults, false)
    })
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  dragover(event) {
    this.preventDefaults(event)
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('border-gray-900', 'bg-gray-50')
      this.dropzoneTarget.classList.remove('border-gray-300')
    }
  }

  dragleave(event) {
    this.preventDefaults(event)
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-gray-900', 'bg-gray-50')
      this.dropzoneTarget.classList.add('border-gray-300')
    }
  }

  drop(event) {
    this.preventDefaults(event)
    this.dragleave(event)

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      this.processFiles(files)
    }
  }

  handleFiles(event) {
    const files = event.target.files
    if (files && files.length > 0) {
      this.processFiles(files)
    }
  }

  processFiles(files) {
    if (!this.hasPreviewTarget) return

    // Validate file count
    if (files.length > 3) {
      this.showNotification('Maximum 3 images allowed', 'error')
      if (this.hasFileInputTarget) {
        this.fileInputTarget.value = ''
      }
      return
    }

    // Clear existing previews
    this.previewTarget.innerHTML = ''
    this.previewTarget.classList.remove('hidden')

    // Validate and preview each file
    const validFiles = []

    Array.from(files).forEach((file, index) => {
      // Validate type
      if (!file.type.startsWith('image/')) {
        this.showNotification(`${file.name} is not an image`, 'error')
        return
      }

      // Validate size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.showNotification(`${file.name} exceeds 5MB limit`, 'error')
        return
      }

      validFiles.push(file)

      // Create preview
      const previewItem = document.createElement('div')
      previewItem.className = 'relative border-2 border-gray-200'

      const reader = new FileReader()
      reader.onload = (e) => {
        const btnClass = 'absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white ' +
          'rounded-full text-xs font-bold flex items-center justify-center hover:bg-red-600'
        previewItem.innerHTML = `
          <img src="${e.target.result}" class="w-full h-24 object-cover" alt="${file.name}">
          <button type="button"
                  class="${btnClass}"
                  data-action="click->dashboard--support-ticket-form#removeFile"
                  data-index="${index}">
            ×
          </button>
          <div class="p-1 text-xs text-gray-500 truncate">${file.name}</div>
        `
      }
      reader.readAsDataURL(file)

      this.previewTarget.appendChild(previewItem)
    })

    // Update file input with valid files only
    if (validFiles.length === 0 && this.hasFileInputTarget) {
      this.fileInputTarget.value = ''
      this.previewTarget.classList.add('hidden')
    }
  }

  removeFile(event) {
    event.preventDefault()

    const index = parseInt(event.currentTarget.dataset.index, 10)
    const previewItems = this.previewTarget.querySelectorAll('div.relative')

    if (previewItems[index]) {
      previewItems[index].remove()
    }

    // If no more files, hide preview and clear input
    if (this.previewTarget.children.length === 0) {
      this.previewTarget.classList.add('hidden')
      if (this.hasFileInputTarget) {
        this.fileInputTarget.value = ''
      }
    }
  }

  showNotification(message, type) {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ?
        'bg-green-50 border-green-500 text-green-700' :
        'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}
